import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const testimonials = [
  {
    id: 1,
    name: 'Jessica & Mark Thompson',
    role: 'Wedding, June 2024',
    image:
      'https://public.readdy.ai/ai/img_res/f46907fa300750b7831cf5b347f706b4.jpg',
    quote:
      'House of FMR transformed our wedding into a fairy tale. The equipment was immaculate, the setup was flawless, and the team went above and beyond. Every guest commented on how stunning everything looked.',
    rating: 5,
    badge: 'Wedding',
  },
  {
    id: 2,
    name: 'David Chen',
    role: 'Corporate Gala, March 2024',
    image:
      'https://readdy.ai/api/search-image?query=professional%20businessman%20in%20suit%20at%20corporate%20event%2C%20confident%20smile%2C%20modern%20event%20venue%20background%2C%20professional%20portrait%20photography%2C%20warm%20lighting&width=400&height=500&seq=event-test-002&orientation=portrait',
    quote:
      'We hired their full AV and lighting package for our annual gala. The production quality was outstanding — it felt like a world‑class event. Their team handled everything seamlessly.',
    rating: 5,
    badge: 'Corporate Event',
  },
  {
    id: 3,
    name: 'Sophie Williams',
    role: '30th Birthday Party, May 2024',
    image:
      'https://readdy.ai/api/search-image?query=stylish%20young%20woman%20smiling%20at%20elegant%20party%2C%20festive%20atmosphere%2C%20beautiful%20party%20decorations%20in%20background%2C%20professional%20portrait%20photography%2C%20warm%20ambient%20lighting&width=400&height=500&seq=event-test-003&orientation=portrait',
    quote:
      'I wanted a chic garden party for my 30th and they delivered beyond my wildest dreams. The furniture, the lighting, the styling — everything was absolutely perfect. Cannot recommend enough!',
    rating: 5,
    badge: 'Private Party',
  },
];

export default function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const { ref: sectionRef, isVisible } = useScrollAnimation({
    rootMargin: '0px 0px -100px 0px',
  });

  // Ensure the index is always a valid integer within bounds
  const safeIndex =
    Number.isInteger(activeIndex) ? activeIndex % testimonials.length : 0;
  const current = testimonials[safeIndex] ?? testimonials[0];

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section
      id="testimonials"
      className="py-32 bg-gradient-to-b from-stone-50 to-white overflow-hidden relative"
    >
      {/* Decorative background blobs */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-amber-100/40 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-20 w-80 h-80 bg-amber-50/50 rounded-full blur-3xl"></div>

      <div ref={sectionRef} className="max-w-7xl mx-auto px-6 relative">
        <div className="grid lg:grid-cols-5 gap-16 items-center">
          {/* Image Side */}
          <div
            className={`lg:col-span-2 relative scroll-animate-left ${
              isVisible ? 'visible' : ''
            }`}
          >
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={current.image}
                  alt={current.name}
                  className="w-full h-[520px] object-cover object-top transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 via-transparent to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <span className="inline-block px-5 py-2 bg-gradient-to-r from-amber-500 to-amber-400 text-white text-sm font-semibold rounded-full shadow-lg">
                    {current.badge}
                  </span>
                </div>
              </div>
              <div className="absolute -inset-4 border border-amber-200/50 rounded-[2rem] -z-10"></div>
            </div>
            <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-gradient-to-br from-amber-300 to-amber-200 rounded-3xl -z-20 shadow-xl"></div>
          </div>

          {/* Content Side */}
          <div
            className={`lg:col-span-3 space-y-10 scroll-animate-right ${
              isVisible ? 'visible' : ''
            }`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-px bg-gradient-to-r from-amber-500 to-transparent"></div>
              <span className="text-amber-600 text-xs font-semibold tracking-[0.25em] uppercase">
                Client Stories
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-display font-normal text-stone-900 leading-tight">
              Trusted by
              <br />
              <span className="text-stone-400 italic">Hundreds of Clients</span>
            </h2>

            <div className="relative">
              <i className="ri-double-quotes-l text-7xl text-amber-200 absolute -top-6 -left-4"></i>
              <p className="text-xl text-stone-700 leading-relaxed pl-10 font-serif italic">
                {current.quote}
              </p>
            </div>

            <div className="flex items-center gap-5 pt-4">
              <div>
                <p className="font-semibold text-stone-900 text-lg">{current.name}</p>
                <p className="text-stone-500">{current.role}</p>
              </div>
              <div className="flex gap-1 ml-auto">
                {Array.from({ length: current.rating }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-500 text-lg"></i>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-5 pt-8 border-t border-stone-200">
              <button
                onClick={prevTestimonial}
                className="w-14 h-14 rounded-full border-2 border-stone-200 flex items-center justify-center text-stone-500 hover:border-amber-400 hover:text-amber-500 hover:bg-amber-50 transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </button>

              <div className="flex gap-3">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={`h-2 rounded-full transition-all cursor-pointer ${
                      index === activeIndex
                        ? 'bg-gradient-to-r from-amber-500 to-amber-400 w-10'
                        : 'bg-stone-200 w-2 hover:bg-stone-300'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={nextTestimonial}
                className="w-14 h-14 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 flex items-center justify-center text-white hover:from-amber-400 hover:to-amber-300 transition-all cursor-pointer shadow-lg shadow-amber-500/20"
              >
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
